import { ApiError } from '../identity/types';
import { classifyFailure } from '../pay-employees/safeErrors';

/**
 * The screen never renders `err.message`. Every failure is reduced to a fixed, plain-language sentence, chosen by the server's stable `code` first and
 * by the HTTP class otherwise (same approach as the Pay Employees check batch).
 */
export interface ContractorBatchFailure {
  /** 409 VERSION_CONFLICT: the batch changed elsewhere, so the screen must reload before the admin continues. */
  versionConflict: boolean;
  message: string;
}

const CODE_COPY: Record<string, string> = {
  VERSION_CONFLICT: 'This batch was changed by someone else. The latest version has been loaded — review it and try again.',
  CHECK_NUMBER_INVALID: 'Enter a check number of 1–20 letters, numbers or hyphens.',
  CHECK_NUMBER_DUPLICATE: 'That check number is already used. Enter a different check number.',
  BATCH_NOT_DRAFT: 'This batch can no longer be changed because it is not a draft.',
  READINESS_REQUIRED: 'Run the readiness check again before continuing — the batch changed since the last check.',
  READINESS_BLOCKED: 'Blocking issues remain. Resolve them and run the readiness check again.',
  WARNINGS_NOT_ACKNOWLEDGED: 'The warnings changed. Review them and acknowledge them again.',
  NO_WARNINGS_TO_ACKNOWLEDGE: 'There are no warnings to acknowledge. Run the readiness check again.',
  REVIEW_NOT_CONFIRMED: 'Confirm that you reviewed the payment details before approving.',
  NO_CONTRACTOR_PAYMENTS: 'There are no contractor payments in this batch.',
  CONTRACTOR_NOT_ACTIVE: 'That contractor is not active, so they can’t be paid.',
  CONTRACTOR_NOT_CHECK_METHOD: 'That contractor is not set to be paid by check.',
  CONTRACTOR_ALREADY_IN_BATCH: 'That contractor is already in this batch. Edit their payment instead.',
  FEATURE_NOT_AVAILABLE_IN_PRODUCTION: 'Approving a contractor payment batch is not available in this release.',
  SEPARATION_OF_DUTIES: 'A batch must be approved by someone other than the person who prepared it. Ask another person who can approve payments to review and approve it.',
};

export function describeContractorFailure(err: unknown, fallback: string): ContractorBatchFailure {
  if (err instanceof ApiError) {
    const known = err.code ? CODE_COPY[err.code] : undefined;
    if (known) return { versionConflict: err.code === 'VERSION_CONFLICT', message: known };
    if (err.status === 400) return { versionConflict: false, message: 'Check the values you entered. A required value is missing or not valid.' };
    if (err.status === 404) return { versionConflict: false, message: 'That record could not be found. Refresh the page and try again.' };
  }
  switch (classifyFailure(err)) {
    case 'forbidden':
      return { versionConflict: false, message: 'You don’t have permission to do this. Ask an administrator to grant you access.' };
    case 'session':
      return { versionConflict: false, message: 'Your session may have expired. Sign in again, then retry.' };
    case 'unavailable':
      return { versionConflict: false, message: 'A connected service isn’t responding right now. Try again shortly.' };
    default:
      return { versionConflict: false, message: fallback };
  }
}
